<!-- Modern Scheduled Orders Filters -->
<div
  class="scheduled-orders-filters modern-filters"
  [class.drawer-mode]="drawerMode"
>
  <!-- Header with Filters title and Clear All button -->
  <div class="filters-header-main" [ngClass]="drawerMode ? 'p-3' : 'mb-3'">
    <div class="header-content">
      <div class="header-left">
        <i class="pi pi-filter text-gray-600 mr-2"></i>
        <h2 class="filters-title">Filters</h2>
      </div>
      <!-- Clear All button positioned right below close icon area -->
      <div class="header-right">
        <button
          class="clear-all-btn"
          [disabled]="loading || !hasActiveFilters"
          [class.has-filters]="hasActiveFilters"
          (click)="onClearFilters()"
          title="Clear all active filters"
        >
          Clear All
        </button>
      </div>
    </div>
  </div>

  <!-- Scrollable Filter Content Container -->
  <div
    class="filters-content-scroll"
    [ngClass]="drawerMode ? 'drawer-scroll' : 'default-scroll'"
  >
    <!-- Search Section -->
    <div class="search-section" [ngClass]="getSearchSectionClass()">
      <!-- Modern Search Input - Full Width -->
      <div class="search-input-wrapper">
        <p-iconfield iconPosition="left" styleClass="w-full">
          <p-inputicon>
            <i class="pi pi-search"></i>
          </p-inputicon>
          <input
            pInputText
            [formControl]="filterForm.controls.search"
            placeholder="Search orders, customers, emails..."
            [disabled]="loading"
            class="modern-search-input w-full"
          />
        </p-iconfield>
      </div>
    </div>

    <!-- Status Section - Always Visible -->
    <div
      class="filter-section status-section"
      [ngClass]="drawerMode ? 'px-3 pb-4' : 'mb-4'"
    >
      <div class="section-header mb-3">
        <h3 class="filter-section-title">Status</h3>
      </div>

      <!-- Status MultiSelect -->
      <div class="mb-3">
        <p-multiSelect
          [options]="statusOptions"
          [formControl]="filterForm.controls.status"
          optionLabel="label"
          optionValue="value"
          placeholder="All statuses"
          [disabled]="loading"
          display="chip"
          appendTo="body"
          [autoZIndex]="true"
          [baseZIndex]="9999"
          class="w-full"
          [maxSelectedLabels]="3"
          selectedItemsLabel="{0} selected"
          styleClass="clean-multiselect"
        />
      </div>

      <!-- Selected Status Summary List -->
      @if (selectedStatusSummary.length > 0) {
        <div class="status-summary-container">
          <span class="status-summary-title"
            >Selected
            {{
              selectedStatusSummary.length === 1 ? "Status" : "Statuses"
            }}</span
          >
          <div class="status-summary-list">
            @for (item of selectedStatusSummary; track item.status) {
              <div
                class="status-summary-item"
                (click)="removeStatus(item.status)"
              >
                <span class="remove-icon">×</span>
                <span class="status-label"
                  >{{ item.status }} ({{ item.count }})</span
                >
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Date Filters Section - Always Visible -->
    <div
      class="filter-section date-section"
      [ngClass]="drawerMode ? 'px-3 pb-4' : 'mb-4'"
    >
      <div class="section-header mb-3">
        <h3 class="filter-section-title">Date Filters</h3>
      </div>

      <!-- Next Occurrence (Most Important) - allows past and future dates -->
      <div class="date-filter-group mb-3">
        <label class="filter-label">Next Occurrence</label>
        <p-datepicker
          [formControl]="filterForm.controls.nextOccurrenceRange"
          selectionMode="range"
          [readonlyInput]="true"
          [showIcon]="true"
          [showClear]="true"
          placeholder="Select date range"
          [disabled]="loading"
          dateFormat="mm/dd/yy"
          class="w-full"
          appendTo="body"
          styleClass="w-full"
        />
      </div>

      <!-- Advanced Date Filters - Minimal by default -->
      @if (showAdvancedDates) {
        <div class="advanced-dates">
          <div class="date-filter-group mb-3">
            <label class="filter-label">Last Processed</label>
            <p-datepicker
              [formControl]="filterForm.controls.lastProcessedRange"
              selectionMode="range"
              [readonlyInput]="true"
              [showIcon]="true"
              [showClear]="true"
              [maxDate]="maxDate"
              placeholder="Select date range"
              [disabled]="loading"
              dateFormat="mm/dd/yy"
              class="w-full"
              appendTo="body"
              styleClass="w-full"
            />
          </div>

          <div class="date-filter-group mb-3">
            <label class="filter-label">Order Created</label>
            <p-datepicker
              [formControl]="filterForm.controls.scheduledOrderCreatedRange"
              selectionMode="range"
              [readonlyInput]="true"
              [showIcon]="true"
              [showClear]="true"
              [maxDate]="maxDate"
              placeholder="Select date range"
              [disabled]="loading"
              dateFormat="mm/dd/yy"
              class="w-full"
              appendTo="body"
              styleClass="w-full"
            />
          </div>

          <div class="date-filter-group mb-3">
            <label class="filter-label">Customer Created</label>
            <p-datepicker
              [formControl]="filterForm.controls.customerCreatedRange"
              selectionMode="range"
              [readonlyInput]="true"
              [showIcon]="true"
              [showClear]="true"
              [maxDate]="maxDate"
              placeholder="Select date range"
              [disabled]="loading"
              dateFormat="mm/dd/yy"
              class="w-full"
              appendTo="body"
              styleClass="w-full"
            />
          </div>
        </div>
      }

      <!-- Toggle for advanced dates -->
      <button
        type="button"
        class="toggle-advanced-btn"
        (click)="toggleAdvancedDates()"
      >
        <i class="pi" [class]="showAdvancedDates ? 'pi-minus' : 'pi-plus'"></i>
        <span>{{ showAdvancedDates ? "Fewer dates" : "More dates" }}</span>
      </button>
    </div>

    <!-- Error Codes Section - Only when Failed status selected -->
    <div
      *ngIf="showProcessingErrorCodes"
      class="filter-section error-section"
      [ngClass]="drawerMode ? 'px-3 pb-4' : 'mb-4'"
    >
      <div class="section-header mb-3">
        <h3 class="filter-section-title">Error Codes</h3>
      </div>

      <p-multiSelect
        [options]="processingErrorCodeOptions"
        [formControl]="filterForm.controls.processingErrorCodes"
        optionLabel="description"
        optionValue="code"
        placeholder="All error codes"
        [disabled]="loading"
        display="chip"
        appendTo="body"
        [autoZIndex]="true"
        [baseZIndex]="9999"
        class="w-full"
        [maxSelectedLabels]="3"
        selectedItemsLabel="{0} selected"
        styleClass="clean-multiselect"
      />
    </div>
  </div>
</div>
